import { Stack, Divider, LinearProgress } from '@mui/joy'
import { useQueryClient } from '@tanstack/react-query'
import { useGate, useStore, useStoreMap } from 'effector-react'
import { useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import { useParams } from 'react-router-dom'
import {
  AddModal,
  EditForm,
  stsDocumentFormModel,
  List,
} from '~/entities/africa/Car/Documents/StsDocument'
import { TypedDropzoneRow } from '~/entities/common/Car/Documents'
import { usePermissions } from '~/entities/common/viewer'
import { CarDocumentType } from '~/shared/config/enums'
import { Alert } from '~/shared/ui/Alerts'
import { ContainerTitle } from '~/shared/ui/Layout'
import {
  formSubmittedCreate,
  Gate,
  createFx,
  $document,
  saveFilesFx,
  saveFiles,
  $loading,
  deleteFile,
  $fileList,
  formSubmittedUpdate,
  updateFx,
} from './model'

export function StsDocument() {
  const { t } = useTranslation()

  const params = useParams<{ carId: string }>()
  const carId = params.carId as string

  useGate(Gate, { carId })

  const { canCarDocumentsUpdate } = usePermissions()
  const queryClient = useQueryClient()
  const isLoading = useStore($loading)
  const isSubmitting = useStore(createFx.pending)
  const isUpdateSubmitting = useStore(updateFx.pending)
  const isSubmittingFiles = useStore(saveFilesFx.pending)
  const document = useStore($document)

  const defaultValues = useStoreMap($document, (document) => {
    const attributes = document?.getAttributes()
    const expirationDate = attributes?.expirationDate
    const isTemporaryNumber = Boolean(attributes?.data?.isTemporaryNumber)

    if (isTemporaryNumber) {
      return {
        issuedDate: attributes?.issuedDate || '',
        issuedBy: attributes?.issuedBy,
        isTemporaryNumber: true as const,
        expirationDate: expirationDate || '',
        plateNumber: attributes?.plateNumber || '',
        number: attributes?.number || null,
      }
    }

    return {
      issuedDate: attributes?.issuedDate || '',
      issuedBy: attributes?.issuedBy,
      isTemporaryNumber: false as const,
      plateNumber: attributes?.plateNumber || '',
      number: attributes?.number || '',
    }
  })

  const fileList = useStore($fileList)

  const handleUpdateList = useCallback(async () => {
    await queryClient.invalidateQueries({
      queryKey: ['sts-document-list'],
    })
  }, [queryClient])

  const handleDocumentCreate = useCallback(
    (formValues: stsDocumentFormModel.FormValues, closeModal: () => void) => {
      formSubmittedCreate({
        formValues,
        fetchList: handleUpdateList,
        closeModal,
      })
    },
    [handleUpdateList],
  )

  return (
    <>
      <Stack
        direction='row'
        justifyContent='space-between'
        alignItems='center'
        mb={2}
      >
        <ContainerTitle>{t('registration certificate')}</ContainerTitle>

        <AddModal
          onSuccess={handleDocumentCreate}
          isSubmitting={isSubmitting}
        />
      </Stack>

      {isLoading && !document && <LinearProgress size='lg' />}

      {document && (
        <>
          <EditForm
            defaultValues={defaultValues}
            canEdit={canCarDocumentsUpdate}
            isSubmitting={isUpdateSubmitting}
            onSuccess={formSubmittedUpdate}
          />
          <Divider sx={{ margin: '24px 0' }} />
          <TypedDropzoneRow
            documentType={CarDocumentType.STS_DOCUMENT}
            thumbs={fileList}
            isSubmitting={isSubmittingFiles}
            onDropAccepted={saveFiles}
            onDelete={deleteFile}
            canDelete={canCarDocumentsUpdate}
            canCreate={canCarDocumentsUpdate}
          />
        </>
      )}

      {!isLoading && !document && (
        <Alert variant='info'>{t('alert.no document')}</Alert>
      )}
      <br />
      <List carId={carId} />
    </>
  )
}
